/**
 * TOTP (Time-based One-Time Password) Implementation for Animated QR Codes
 *
 * Generates rotating codes every 15 seconds for anti-fraud protection.
 * Based on RFC 6238 - TOTP: Time-Based One-Time Password Algorithm
 */

// TOTP Configuration
const TOTP_WINDOW = 15 // 15-second rotation window
const CACHE_SIZE = 10 // Pre-generate 10 codes (2.5 minutes ahead)

/**
 * Simple HMAC-SHA256 implementation using Web Crypto API
 */
async function hmacSHA256(key: string, message: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder()
  const keyData = encoder.encode(key)
  const messageData = encoder.encode(message)

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  return crypto.subtle.sign('HMAC', cryptoKey, messageData)
}

/**
 * Generate TOTP code for a specific time window
 */
async function generateTOTPCode(secret: string, timeWindow: number): Promise<string> {
  // Create message from time window
  const message = timeWindow.toString()

  // Generate HMAC
  const hmac = await hmacSHA256(secret, message)
  const hmacArray = new Uint8Array(hmac)

  // Dynamic truncation (extract 6-digit code)
  const offset = hmacArray[hmacArray.length - 1] & 0x0f
  const code =
    ((hmacArray[offset] & 0x7f) << 24) |
    ((hmacArray[offset + 1] & 0xff) << 16) |
    ((hmacArray[offset + 2] & 0xff) << 8) |
    (hmacArray[offset + 3] & 0xff)

  // Return 6-digit code
  return (code % 1000000).toString().padStart(6, '0')
}

/**
 * Get current time window (15-second intervals)
 */
export function getCurrentTimeWindow(): number {
  return Math.floor(Date.now() / 1000 / TOTP_WINDOW)
}

/**
 * Get time remaining in current window (in seconds)
 */
export function getTimeRemainingInWindow(): number {
  const currentTime = Math.floor(Date.now() / 1000)
  const windowStart = Math.floor(currentTime / TOTP_WINDOW) * TOTP_WINDOW
  const windowEnd = windowStart + TOTP_WINDOW
  return windowEnd - currentTime
}

/**
 * Generate QR code data with TOTP
 */
export async function generateQRCodeData(orderId: string, secret: string): Promise<string> {
  const timeWindow = getCurrentTimeWindow()
  const totpCode = await generateTOTPCode(secret, timeWindow)

  // QR code format: ORDER_ID|TOTP_CODE|WINDOW
  return `${orderId}|${totpCode}|${timeWindow}`
}

/**
 * Pre-generate multiple TOTP codes for offline use
 */
export async function preGenerateCodes(orderId: string, secret: string): Promise<Array<{
  qrData: string
  timeWindow: number
  validUntil: Date
}>> {
  const codes = []
  const baseWindow = getCurrentTimeWindow()

  for (let i = 0; i < CACHE_SIZE; i++) {
    const timeWindow = baseWindow + i
    const totpCode = await generateTOTPCode(secret, timeWindow)
    const qrData = `${orderId}|${totpCode}|${timeWindow}`
    const validUntil = new Date((timeWindow + 1) * TOTP_WINDOW * 1000)

    codes.push({
      qrData,
      timeWindow,
      validUntil
    })
  }

  return codes
}

/**
 * Verify TOTP code (allows for time drift)
 */
export async function verifyTOTPCode(
  orderId: string,
  providedCode: string,
  secret: string,
  allowedDrift: number = 1
): Promise<boolean> {
  const currentWindow = getCurrentTimeWindow()

  // Check current window and adjacent windows
  for (let i = -allowedDrift; i <= allowedDrift; i++) {
    const window = currentWindow + i
    const expectedCode = await generateTOTPCode(secret, window)
    const expectedQRData = `${orderId}|${expectedCode}|${window}`

    if (providedCode === expectedQRData) {
      return true
    }
  }

  return false
}

/**
 * Generate a secure secret for TOTP
 * This should be called once per order and stored in the database
 */
export function generateTOTPSecret(): string {
  // Generate 32-byte (256-bit) random secret
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)

  // Convert to base64 for storage
  return btoa(String.fromCharCode.apply(null, Array.from(array)))
}

/**
 * QR Code Cache Manager for offline support
 */
export class QRCodeCache {
  private cache: Map<number, string> = new Map()
  private orderId: string
  private secret: string

  constructor(orderId: string, secret: string) {
    this.orderId = orderId
    this.secret = secret
  }

  /**
   * Initialize cache with pre-generated codes
   */
  async initialize(): Promise<void> {
    const codes = await preGenerateCodes(this.orderId, this.secret)
    codes.forEach(code => {
      this.cache.set(code.timeWindow, code.qrData)
    })
  }

  /**
   * Get QR code for current time window
   */
  getCode(timeWindow?: number): string | undefined {
    const window = timeWindow ?? getCurrentTimeWindow()
    return this.cache.get(window)
  }

  /**
   * Check if cache needs refresh
   */
  needsRefresh(): boolean {
    const currentWindow = getCurrentTimeWindow()
    const cacheWindows = Array.from(this.cache.keys())
    const maxWindow = Math.max(...cacheWindows)

    // Refresh if we're within 3 windows of running out
    return maxWindow - currentWindow < 3
  }

  /**
   * Refresh cache by generating new codes
   */
  async refresh(): Promise<void> {
    const currentWindow = getCurrentTimeWindow()

    // Remove old codes
    for (const window of this.cache.keys()) {
      if (window < currentWindow) {
        this.cache.delete(window)
      }
    }

    // Add new codes
    const maxWindow = Math.max(...Array.from(this.cache.keys()))
    for (let i = 1; i <= 5; i++) {
      const window = maxWindow + i
      const totpCode = await generateTOTPCode(this.secret, window)
      const qrData = `${this.orderId}|${totpCode}|${window}`
      this.cache.set(window, qrData)
    }
  }

  /**
   * Save cache to localStorage for offline use
   */
  saveToStorage(): void {
    const cacheData = {
      orderId: this.orderId,
      codes: Array.from(this.cache.entries())
    }
    localStorage.setItem(`qr_cache_${this.orderId}`, JSON.stringify(cacheData))
  }

  /**
   * Load cache from localStorage
   */
  loadFromStorage(): boolean {
    const stored = localStorage.getItem(`qr_cache_${this.orderId}`)
    if (!stored) return false

    try {
      const cacheData = JSON.parse(stored)
      this.cache = new Map(cacheData.codes)
      return true
    } catch (error) {
      console.error('Failed to load QR cache:', error)
      return false
    }
  }
}
